import React, { useState, useMemo } from 'react';
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as BarTooltip,
    LineChart, Line, AreaChart, Area
} from 'recharts';
import Swal from 'sweetalert2';
import {
    useListExpensesQuery,
    useExpenseSummaryQuery,
    useTopExpenseCategoryQuery,
    useFinancialOverviewQuery,
    useGetOpeningBalanceQuery,
    useSetOpeningBalanceMutation,
    useCreateExpenseMutation,
    useUpdateExpenseMutation,
    useDeleteExpenseMutation,
} from '../../../../api/expense.api';
import { formatCurrency, formatNumber } from '../dashboardUtils';

/* ─── CONSTANTS ─────────────────────────────────────────────────────────────── */
const EXPENSE_CATEGORIES = ['Marketing', 'Inventory', 'Logistics', 'Utilities', 'Other'];
const EXPENSE_TYPES = ['expense', 'loan', 'salary', 'rent', 'due'];
const STATUSES = ['pending', 'partial', 'paid'];
const COLORS = ['#0f172a', '#059669', '#6366f1', '#d97706', '#e11d48', '#0891b2', '#8b5cf6', '#64748b'];

const STATUS_STYLE = {
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    pending: 'bg-rose-50 text-rose-600 border-rose-200',
};

const TYPE_LABEL = {
    expense: 'Expense', loan: 'Loan', salary: 'Salary', rent: 'Rent', due: 'Due'
};
const TYPE_COLOR = {
    expense: '#0f172a', loan: '#e11d48', salary: '#059669', rent: '#d97706', due: '#6366f1'
};
const TYPE_ICON = {
    expense: '💸', loan: '🏦', salary: '👤', rent: '🏠', due: '📋'
};

/* ─── EMPTY FORM FACTORY ─────────────────────────────────────────────────────── */
const makeEmptyForm = (type = 'expense') => ({
    title: '',
    amount: '',
    category: type === 'expense' ? 'Marketing' : '',
    type,
    party: '',
    status: 'pending',
    paidAmount: '',
    period: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
});

/* ─── HELPERS ────────────────────────────────────────────────────────────────── */
const Spinner = () => (
    <div className="flex items-center justify-center py-10">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-stone-700 rounded-full animate-spin" />
    </div>
);

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide border ${className}`}>
        {children}
    </span>
);

const TypeBadge = ({ type }) => {
    const colors = {
        expense: 'bg-stone-100 text-stone-700 border-stone-200',
        loan: 'bg-rose-50 text-rose-600 border-rose-200',
        salary: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        rent: 'bg-amber-50 text-amber-700 border-amber-200',
        due: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    };
    return <Badge className={colors[type] || colors.expense}>{TYPE_ICON[type]} {TYPE_LABEL[type]}</Badge>;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-NP', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/* ─── STAT CARD ──────────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon, accent = '#0f172a', loading, trend, negative }) => (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg" style={{ background: accent }}>
                {icon}
            </span>
            {sub && (
                <span className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-stone-50 text-stone-500 border border-stone-100 max-w-[120px] text-right leading-tight">
                    {sub}
                </span>
            )}
        </div>
        {loading ? (
            <div className="h-8 bg-stone-100 rounded-lg animate-pulse w-2/3" />
        ) : (
            <div>
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-0.5">{label}</p>
                <p className={`text-2xl font-bold leading-tight ${negative ? 'text-rose-600' : 'text-stone-800'}`}>{value}</p>
                {trend && <p className="text-[10px] text-stone-400 mt-1">{trend}</p>}
            </div>
        )}
    </div>
);

/* ─── EXPENSE MODAL ──────────────────────────────────────────────────────────── */
const ExpenseModal = ({ mode = 'add', initial, onClose, onSave, saving }) => {
    const [form, setForm] = useState(initial);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const isLoan = form.type === 'loan';
    const isDue = form.type === 'due';
    const isSalary = form.type === 'salary';
    const isRent = form.type === 'rent';
    const isExpense = form.type === 'expense';
    const needsParty = isLoan || isDue || isSalary || isRent;
    const needsStatus = isLoan || isDue;
    const needsPaidAmount = (isLoan || isDue) && (form.status === 'partial' || form.status === 'paid');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.title || !form.amount) {
            Swal.fire('Validation', 'Title and Amount are required.', 'warning'); return;
        }
        onSave({
            ...form,
            amount: parseFloat(form.amount),
            paidAmount: form.paidAmount ? parseFloat(form.paidAmount) : 0
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-stone-100 overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 flex-shrink-0">
                    <div>
                        <h3 className="text-sm font-bold text-stone-900">
                            {mode === 'edit' ? '✏️ Edit Entry' : `+ New ${TYPE_LABEL[form.type] || 'Entry'}`}
                        </h3>
                        <p className="text-[10px] text-stone-400 mt-0.5">Fill in the details below</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700 text-xl leading-none transition">×</button>
                </div>

                <div className="overflow-y-auto flex-1">
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
                        {/* Type selector — only when adding */}
                        {mode === 'add' && (
                            <div>
                                <label className="block font-semibold text-stone-700 mb-2">Entry Type</label>
                                <div className="flex flex-wrap gap-2">
                                    {EXPENSE_TYPES.map(t => (
                                        <button
                                            key={t} type="button"
                                            onClick={() => { set('type', t); set('category', t === 'expense' ? 'Marketing' : ''); set('status', 'pending'); }}
                                            className={`px-3 py-1.5 rounded-lg border text-[11px] font-semibold transition ${form.type === t ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-600 hover:border-stone-400'}`}
                                        >
                                            {TYPE_ICON[t]} {TYPE_LABEL[t]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Title */}
                        <div>
                            <label className="block font-semibold text-stone-700 mb-1">
                                {isSalary ? 'Employee Name' : isDue ? 'Due Title' : isLoan ? 'Loan Purpose' : isRent ? 'Rent For' : 'Expense Title'}
                                <span className="text-rose-500"> *</span>
                            </label>
                            <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
                                placeholder={isSalary ? 'e.g. Ramesh Thapa' : isLoan ? 'e.g. Stock Purchase Loan' : 'e.g. Meta Ad Spend'}
                                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                            />
                        </div>

                        {/* Party / Person */}
                        {needsParty && (
                            <div>
                                <label className="block font-semibold text-stone-700 mb-1">
                                    {isLoan ? 'Lender / Bank' : isDue ? 'Vendor / Supplier' : isSalary ? 'Position / Role' : 'Landlord Name'}
                                </label>
                                <input type="text" value={form.party} onChange={e => set('party', e.target.value)}
                                    placeholder={isLoan ? 'e.g. Nabil Bank' : isDue ? 'e.g. Fabric Supplier A' : isSalary ? 'e.g. Manager' : 'e.g. Ram Bahadur'}
                                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                />
                            </div>
                        )}

                        {/* Salary period */}
                        {isSalary && (
                            <div>
                                <label className="block font-semibold text-stone-700 mb-1">Pay Period</label>
                                <input type="text" value={form.period} onChange={e => set('period', e.target.value)}
                                    placeholder="e.g. August 2025"
                                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                />
                            </div>
                        )}

                        {/* Amount + Category (for expenses) */}
                        <div className={`grid gap-3 ${isExpense ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            <div>
                                <label className="block font-semibold text-stone-700 mb-1">
                                    {isLoan ? 'Loan Amount (NPR)' : isDue ? 'Due Amount (NPR)' : 'Amount (NPR)'}
                                    <span className="text-rose-500"> *</span>
                                </label>
                                <input type="number" min="1" value={form.amount} onChange={e => set('amount', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                />
                            </div>
                            {isExpense && (
                                <div>
                                    <label className="block font-semibold text-stone-700 mb-1">Category</label>
                                    <select value={form.category} onChange={e => set('category', e.target.value)}
                                        className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                    >
                                        {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Loan/Due status + paid amount */}
                        {needsStatus && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block font-semibold text-stone-700 mb-1">Repayment Status</label>
                                    <select value={form.status} onChange={e => set('status', e.target.value)}
                                        className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                    >
                                        {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                                    </select>
                                </div>
                                {needsPaidAmount && (
                                    <div>
                                        <label className="block font-semibold text-stone-700 mb-1">Amount Paid (NPR)</label>
                                        <input type="number" min="0" value={form.paidAmount} onChange={e => set('paidAmount', e.target.value)}
                                            placeholder="0"
                                            className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Date */}
                        <div>
                            <label className="block font-semibold text-stone-700 mb-1">Date</label>
                            <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                            />
                        </div>

                        {/* Note */}
                        <div>
                            <label className="block font-semibold text-stone-700 mb-1">Note</label>
                            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
                                placeholder="Optional details…"
                                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition resize-none"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={onClose} className="px-4 py-2 border border-stone-200 rounded-xl font-semibold text-stone-600 hover:bg-stone-50 transition">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving}
                                className="px-5 py-2 bg-stone-900 text-white rounded-xl font-semibold hover:bg-stone-700 disabled:opacity-50 transition"
                            >
                                {saving ? 'Saving…' : mode === 'edit' ? 'Update' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

/* ─── OPENING BALANCE MODAL ──────────────────────────────────────────────────── */
const OpeningBalanceModal = ({ current, onClose }) => {
    const [amount, setAmount] = useState(current?.amount ?? 0);
    const [note, setNote] = useState(current?.note ?? '');
    const [setBalance, { isLoading }] = useSetOpeningBalanceMutation();

    const handleSave = async () => {
        try {
            await setBalance({ amount: parseFloat(amount), note }).unwrap();
            Swal.fire({ icon: 'success', title: 'Balance Updated', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
            onClose();
        } catch (err) {
            Swal.fire('Error', err?.data?.message || 'Failed to update', 'error');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl border border-stone-100 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
                    <div>
                        <h3 className="text-sm font-bold text-stone-900">💰 Set Opening Balance</h3>
                        <p className="text-[10px] text-stone-400 mt-0.5">Your previous / carry-forward cash balance</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700 text-xl transition">×</button>
                </div>
                <div className="p-6 space-y-4 text-xs">
                    <div>
                        <label className="block font-semibold text-stone-700 mb-1">Balance Amount (NPR) <span className="text-rose-500">*</span></label>
                        <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                            placeholder="e.g. 50000"
                        />
                    </div>
                    <div>
                        <label className="block font-semibold text-stone-700 mb-1">Note</label>
                        <input type="text" value={note} onChange={e => setNote(e.target.value)}
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 transition"
                            placeholder="e.g. Balance as of Jan 2025"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="px-4 py-2 border border-stone-200 rounded-xl font-semibold text-stone-600 hover:bg-stone-50 transition">Cancel</button>
                        <button onClick={handleSave} disabled={isLoading}
                            className="px-5 py-2 bg-stone-900 text-white rounded-xl font-semibold hover:bg-stone-700 disabled:opacity-50 transition"
                        >
                            {isLoading ? 'Saving…' : 'Save Balance'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── LEDGER TABLE (shared for all tabs) ─────────────────────────────────────── */
const LedgerTable = ({ type, dateRange, onEdit, onDelete }) => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const [statusFilter, setStatusFilter] = useState('');
    const { from, to } = dateRange;

    const { data: listData, isLoading, isFetching } = useListExpensesQuery({
        page, limit: 20, type, from, to, search
    });

    const allRows = listData?.result || [];
    const totalCount = listData?.meta?.total ?? allRows.length;

    const displayRows = useMemo(() => {
        let arr = [...allRows];
        if (statusFilter) arr = arr.filter(r => r.status === statusFilter);
        if (sortBy === 'amount_desc') arr.sort((a, b) => b.amount - a.amount);
        else if (sortBy === 'amount_asc') arr.sort((a, b) => a.amount - b.amount);
        else if (sortBy === 'date_asc') arr.sort((a, b) => new Date(a.date) - new Date(b.date));
        else arr.sort((a, b) => new Date(b.date) - new Date(a.date));
        return arr;
    }, [allRows, sortBy, statusFilter]);

    const hasStatus = type === 'loan' || type === 'due';
    const hasParty = type !== 'expense';
    const hasPeriod = type === 'salary';
    const hasCategory = type === 'expense';

    return (
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
            {/* Toolbar */}
            <div className="p-5 border-b border-stone-50 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                    <p className="text-xs font-bold text-stone-800">{TYPE_LABEL[type]} Ledger</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">
                        {isFetching ? 'Refreshing…' : `${totalCount} records`}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                        <svg className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input type="text" placeholder="Search…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="pl-7 pr-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-300 w-36"
                        />
                    </div>
                    {/* Status filter (loans/dues) */}
                    {hasStatus && (
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-300"
                        >
                            <option value="">All Status</option>
                            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                    )}
                    {/* Sort */}
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                        className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-300"
                    >
                        <option value="date_desc">Newest First</option>
                        <option value="date_asc">Oldest First</option>
                        <option value="amount_desc">Highest Amount</option>
                        <option value="amount_asc">Lowest Amount</option>
                    </select>
                </div>
            </div>

            {isLoading ? <Spinner /> : (
                <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-stone-50 text-left">
                                    <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">#</th>
                                    <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">Title</th>
                                    {hasParty && <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">
                                        {type === 'loan' ? 'Lender' : type === 'due' ? 'Vendor' : type === 'salary' ? 'Employee' : 'Landlord'}
                                    </th>}
                                    {hasPeriod && <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">Period</th>}
                                    {hasCategory && <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">Category</th>}
                                    {hasStatus && <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">Status</th>}
                                    <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider">Date</th>
                                    <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider text-right">Amount</th>
                                    {hasStatus && <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider text-right">Outstanding</th>}
                                    <th className="px-5 py-3 text-[10px] font-bold text-stone-500 uppercase tracking-wider text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-50">
                                {displayRows.length === 0 ? (
                                    <tr><td colSpan={9} className="py-12 text-center text-xs text-stone-400">No records found.</td></tr>
                                ) : displayRows.map((row, idx) => {
                                    const outstanding = (row.amount || 0) - (row.paidAmount || 0);
                                    return (
                                        <tr key={row._id || row.id} className="hover:bg-stone-50/70 transition-colors group">
                                            <td className="px-5 py-3.5 text-xs text-stone-400 font-medium">{(page - 1) * 20 + idx + 1}</td>
                                            <td className="px-5 py-3.5">
                                                <p className="text-xs font-semibold text-stone-900">{row.title}</p>
                                                {row.description && <p className="text-[10px] text-stone-400 truncate max-w-[200px]">{row.description}</p>}
                                            </td>
                                            {hasParty && <td className="px-5 py-3.5 text-xs text-stone-600">{row.party || '—'}</td>}
                                            {hasPeriod && <td className="px-5 py-3.5 text-xs text-stone-600">{row.period || '—'}</td>}
                                            {hasCategory && (
                                                <td className="px-5 py-3.5">
                                                    <Badge className="bg-stone-100 text-stone-600 border-stone-200">{row.category || 'Other'}</Badge>
                                                </td>
                                            )}
                                            {hasStatus && (
                                                <td className="px-5 py-3.5">
                                                    <Badge className={STATUS_STYLE[row.status] || STATUS_STYLE.pending}>
                                                        {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || 'Pending'}
                                                    </Badge>
                                                </td>
                                            )}
                                            <td className="px-5 py-3.5 text-xs text-stone-500">{fmtDate(row.date)}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <span className="text-xs font-bold text-stone-800">{formatCurrency(row.amount)}</span>
                                            </td>
                                            {hasStatus && (
                                                <td className="px-5 py-3.5 text-right">
                                                    <span className={`text-xs font-bold ${outstanding > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                        {outstanding > 0 ? formatCurrency(outstanding) : '✓ Cleared'}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="px-5 py-3.5 text-center">
                                                <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => onEdit(row)} className="p-1.5 rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all" title="Edit">
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                    </button>
                                                    <button onClick={() => onDelete(row._id || row.id)} className="p-1.5 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-all" title="Delete">
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y divide-stone-50">
                        {displayRows.length === 0 ? (
                            <p className="py-10 text-center text-xs text-stone-400">No records found.</p>
                        ) : displayRows.map(row => (
                            <div key={row._id || row.id} className="px-4 py-3.5 flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-xs font-semibold text-stone-900">{row.title}</p>
                                        {hasStatus && <Badge className={STATUS_STYLE[row.status] || STATUS_STYLE.pending}>{row.status}</Badge>}
                                    </div>
                                    {hasParty && row.party && <p className="text-[10px] text-stone-500 mt-0.5">{row.party}</p>}
                                    {hasPeriod && row.period && <p className="text-[10px] text-stone-500 mt-0.5">{row.period}</p>}
                                    <p className="text-[10px] text-stone-400 mt-1">{fmtDate(row.date)}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1.5">
                                    <span className="text-sm font-bold text-stone-800">{formatCurrency(row.amount)}</span>
                                    {hasStatus && (row.amount - (row.paidAmount || 0)) > 0 && (
                                        <span className="text-[10px] text-rose-600 font-semibold">
                                            Outstanding: {formatCurrency(row.amount - (row.paidAmount || 0))}
                                        </span>
                                    )}
                                    <div className="flex gap-1.5">
                                        <button onClick={() => onEdit(row)} className="p-1.5 rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                        </button>
                                        <button onClick={() => onDelete(row._id || row.id)} className="p-1.5 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-all">
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalCount > 20 && (
                        <div className="px-5 py-3.5 border-t border-stone-50 flex items-center justify-between">
                            <p className="text-[10px] text-stone-400">Page {page} · {Math.ceil(totalCount / 20)} pages</p>
                            <div className="flex gap-2">
                                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                    className="px-3 py-1.5 text-xs border border-stone-200 rounded-lg disabled:opacity-40 hover:bg-stone-50 transition">← Prev</button>
                                <button disabled={page >= Math.ceil(totalCount / 20)} onClick={() => setPage(p => p + 1)}
                                    className="px-3 py-1.5 text-xs border border-stone-200 rounded-lg disabled:opacity-40 hover:bg-stone-50 transition">Next →</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/* ─── PIE TOOLTIP ────────────────────────────────────────────────────────────── */
const PieTooltipContent = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-stone-900 text-white text-xs rounded-xl px-3 py-2 shadow-lg">
            <p className="font-semibold">{payload[0].name}</p>
            <p>{formatCurrency(payload[0].value)}</p>
            <p className="text-stone-400">{payload[0].payload.percentage?.toFixed(1)}%</p>
        </div>
    );
};

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────── */
const ExpenseManagementView = ({ dateRange }) => {
    const { from, to } = dateRange;

    const [activeTab, setActiveTab] = useState('overview');
    const [modal, setModal] = useState(null);
    const [showBalanceModal, setShowBalanceModal] = useState(false);

    /* Queries */
    const { data: overviewData, isLoading: overviewLoading } = useFinancialOverviewQuery({ from, to });
    const { data: balanceData } = useGetOpeningBalanceQuery();
    const { data: topData, isLoading: topLoading } = useTopExpenseCategoryQuery({ from, to });

    /* Mutations */
    const [createExpense, { isLoading: isCreating }] = useCreateExpenseMutation();
    const [updateExpense, { isLoading: isUpdating }] = useUpdateExpenseMutation();
    const [deleteExpense] = useDeleteExpenseMutation();

    /* Derived */
    const ov = overviewData?.result || {};
    const topCategories = Array.isArray(topData?.result) ? topData.result : [];
    const pieData = topCategories.map(c => ({ name: c.category || 'Other', value: c.totalAmount || 0, percentage: c.percentage || 0 }));
    const barData = [...topCategories].sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 6).map(c => ({ name: c.category, amount: c.totalAmount }));
    const cashNegative = (ov.cashBalance || 0) < 0;

    /* Handlers */
    const openAdd = (type = 'expense') => setModal({ mode: 'add', initial: makeEmptyForm(type) });
    const openEdit = (row) => setModal({
        mode: 'edit',
        initial: {
            title: row.title || '',
            amount: row.amount || '',
            category: row.category || '',
            type: row.type || 'expense',
            party: row.party || '',
            status: row.status || 'pending',
            paidAmount: row.paidAmount || '',
            period: row.period || '',
            date: row.date ? new Date(row.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            description: row.description || '',
            _id: row._id || row.id,
        }
    });
    const closeModal = () => setModal(null);

    const handleSave = async (formData) => {
        try {
            if (modal.mode === 'edit') {
                await updateExpense({ id: formData._id, ...formData }).unwrap();
                Swal.fire({ icon: 'success', title: 'Updated', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
            } else {
                await createExpense(formData).unwrap();
                Swal.fire({ icon: 'success', title: 'Saved', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
            }
            closeModal();
        } catch (err) {
            Swal.fire('Error', err?.data?.message || 'Something went wrong', 'error');
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await Swal.fire({ title: 'Delete this record?', text: 'This cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e11d48', confirmButtonText: 'Delete' });
        if (confirmed.isConfirmed) {
            try {
                await deleteExpense(id).unwrap();
                Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
            } catch (err) {
                Swal.fire('Error', err?.data?.message || 'Delete failed', 'error');
            }
        }
    };

    /* Tab config */
    const TABS = [
        { id: 'overview', label: '📊 Overview' },
        { id: 'expense', label: '💸 Expenses' },
        { id: 'salary', label: '👤 Salaries' },
        { id: 'loan', label: '🏦 Loans' },
        { id: 'rent', label: '🏠 Rent' },
        { id: 'due', label: '📋 Dues' },
    ];

    return (
        <div className="space-y-6 w-full">

            {/* ── Page Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold text-stone-900">Financial Management</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Complete money tracking — expenses, salaries, loans, rent & dues</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setShowBalanceModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 border border-stone-200 rounded-xl text-xs font-semibold text-stone-700 hover:bg-stone-50 transition-all shadow-sm"
                    >
                        💰 Set Opening Balance
                    </button>
                    <button
                        onClick={() => openAdd(activeTab === 'overview' ? 'expense' : activeTab)}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-xl text-xs font-semibold hover:bg-stone-700 transition-all shadow-sm"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Add {activeTab === 'overview' ? 'Entry' : TYPE_LABEL[activeTab]}
                    </button>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-stone-900 text-white shadow-sm' : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-400 hover:text-stone-800'}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ══════════════════ OVERVIEW TAB ══════════════════ */}
            {activeTab === 'overview' && (
                <div className="space-y-6">

                    {/* KPI Strip — row 1: money flow */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="💵 Cash Balance" value={formatCurrency(ov.cashBalance || 0)} accent={cashNegative ? '#e11d48' : '#059669'} loading={overviewLoading}
                            trend={`Opening ${formatCurrency(ov.openingBalance || 0)} + Revenue ${formatCurrency(ov.totalRevenue || 0)}`}
                            negative={cashNegative}
                            icon="💵"
                            sub={cashNegative ? '⚠ Deficit' : 'Available Cash'}
                        />
                        <StatCard label="Order Revenue" value={formatCurrency(ov.totalRevenue || 0)} accent="#059669" loading={overviewLoading}
                            sub={`${ov.totalOrders || 0} orders`} icon="📦"
                        />
                        <StatCard label="Total Spent" value={formatCurrency(ov.totalExpenses || 0)} accent="#e11d48" loading={overviewLoading}
                            sub="All categories" icon="📤"
                        />
                        <StatCard label="Opening Balance" value={formatCurrency(ov.openingBalance || 0)} accent="#6366f1" loading={overviewLoading}
                            sub={balanceData?.result?.note || 'Carry-forward'} icon="🏦"
                        />
                    </div>

                    {/* KPI Strip — row 2: expenditure breakdown */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        <StatCard label="General Expenses" value={formatCurrency(ov.generalExpenses || 0)} accent="#0f172a" loading={overviewLoading} icon="💸" />
                        <StatCard label="Salaries" value={formatCurrency(ov.totalSalary || 0)} accent="#059669" loading={overviewLoading} icon="👤" />
                        <StatCard label="Loans Taken" value={formatCurrency(ov.totalLoans || 0)} accent="#e11d48" loading={overviewLoading}
                            sub={`Outstanding: ${formatCurrency(ov.outstandingLoans || 0)}`} icon="🏦"
                        />
                        <StatCard label="Rent Paid" value={formatCurrency(ov.totalRent || 0)} accent="#d97706" loading={overviewLoading} icon="🏠" />
                        <StatCard label="Dues" value={formatCurrency(ov.totalDues || 0)} accent="#6366f1" loading={overviewLoading}
                            sub={`Unpaid: ${formatCurrency(ov.unpaidDues || 0)}`} icon="📋"
                        />
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Pie: expense by category */}
                        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
                            <p className="text-xs font-bold text-stone-800 mb-1">Expense Breakdown by Category</p>
                            <p className="text-[10px] text-stone-400 mb-4">General expenses only</p>
                            {topLoading ? <Spinner /> : pieData.length === 0 ? (
                                <div className="h-56 flex items-center justify-center text-xs text-stone-400">No data</div>
                            ) : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                                                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Pie>
                                            <ReTooltip content={<PieTooltipContent />} />
                                            <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* Bar: expenditure by type */}
                        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
                            <p className="text-xs font-bold text-stone-800 mb-1">Spend by Type</p>
                            <p className="text-[10px] text-stone-400 mb-4">Expenses vs Salary vs Loans vs Rent vs Dues</p>
                            {overviewLoading ? <Spinner /> : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart barSize={36} data={[
                                            { name: 'Expenses', amount: ov.generalExpenses || 0 },
                                            { name: 'Salary', amount: ov.totalSalary || 0 },
                                            { name: 'Loans', amount: ov.totalLoans || 0 },
                                            { name: 'Rent', amount: ov.totalRent || 0 },
                                            { name: 'Dues', amount: ov.totalDues || 0 },
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                            <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                            <BarTooltip contentStyle={{ backgroundColor: '#0f172a', borderRadius: '0.75rem', color: '#f8fafc', fontSize: '11px', border: 'none' }} formatter={v => [formatCurrency(v), 'Amount']} />
                                            <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                                                {['#0f172a', '#059669', '#e11d48', '#d97706', '#6366f1'].map((c, i) => <Cell key={i} fill={c} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Liabilities alert if outstanding */}
                    {((ov.outstandingLoans || 0) > 0 || (ov.unpaidDues || 0) > 0) && (
                        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5">
                            <p className="text-xs font-bold text-rose-800 mb-3">⚠ Outstanding Liabilities</p>
                            <div className="grid grid-cols-2 gap-4">
                                {(ov.outstandingLoans || 0) > 0 && (
                                    <div className="flex items-center gap-3">
                                        <span className="w-8 h-8 bg-rose-100 rounded-xl flex items-center justify-center text-sm">🏦</span>
                                        <div>
                                            <p className="text-[10px] text-rose-600 font-semibold uppercase tracking-wider">Loan Outstanding</p>
                                            <p className="text-base font-bold text-rose-700">{formatCurrency(ov.outstandingLoans)}</p>
                                        </div>
                                    </div>
                                )}
                                {(ov.unpaidDues || 0) > 0 && (
                                    <div className="flex items-center gap-3">
                                        <span className="w-8 h-8 bg-rose-100 rounded-xl flex items-center justify-center text-sm">📋</span>
                                        <div>
                                            <p className="text-[10px] text-rose-600 font-semibold uppercase tracking-wider">Unpaid Dues</p>
                                            <p className="text-base font-bold text-rose-700">{formatCurrency(ov.unpaidDues)}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════ LEDGER TABS ══════════════════ */}
            {activeTab !== 'overview' && (
                <LedgerTable
                    type={activeTab}
                    dateRange={dateRange}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                />
            )}

            {/* ── Modals ── */}
            {modal && (
                <ExpenseModal
                    mode={modal.mode}
                    initial={modal.initial}
                    onClose={closeModal}
                    onSave={handleSave}
                    saving={isCreating || isUpdating}
                />
            )}
            {showBalanceModal && (
                <OpeningBalanceModal
                    current={balanceData?.result}
                    onClose={() => setShowBalanceModal(false)}
                />
            )}
        </div>
    );
};

export default ExpenseManagementView;
